import {DEFAULT_PAGE_SIZE, FetchDataError, GenericErrors, interpolateRoute, Loader, request} from 'apstra-ui-common';
import {observer} from 'mobx-react';
import {FC, ReactNode, useContext, useMemo} from 'react';
import {Link} from 'react-router-dom';

import {useQuery} from '@tanstack/react-query';

import IBAContext from '../IBAContext';
import {apiQueryOptions} from '../../apiQueryOptions';
import generateProbeURI from '../generateProbeURI';
import {queryParamToSorting} from '../../queryParamUtils';
import {
  getStageQueryParamsFromWidget, getStageRenderingStrategy,
  castFilterConverter, getStageComponentPropsFromWidget, getValueColumnNameFromWidget,
} from '../stageUtils';
import {Widget} from '../types';

import ProbeStage from './ProbeStage';

type StageWidgetProps = {
  loaderContent?: ReactNode;
  widget: Widget;
};

const StageWidget: FC<StageWidgetProps> = observer(({
  loaderContent,
  widget,
}) => {
  const {blueprintId, routes} = useContext(IBAContext);
  const probeDetailsQuery = useQuery({
    ...apiQueryOptions.probeDetails(blueprintId, widget.probe_id),
    queryFn: ({signal}) => request(
      interpolateRoute(routes.probeDetails, {blueprintId, probeId: widget.probe_id}),
      {signal}
    ),
  });

  const {probe, processor, stage} = useMemo(
    () => getStageComponentPropsFromWidget({widget, probes: [probeDetailsQuery.data]}),
    [widget, probeDetailsQuery.data]
  );

  const valueColumnName = useMemo(
    () => getValueColumnNameFromWidget({widget, stage}),
    [stage, widget]
  );

  const renderingStrategy = useMemo(
    () => probe && stage && getStageRenderingStrategy({
      probe,
      stage,
      dataSource: widget.data_source,
      showContext: widget.show_context
    }),
    [probe, stage, widget.data_source, widget.show_context]
  );

  if (probeDetailsQuery.isLoading) {
    return loaderContent ?? <Loader />;
  }

  if (probeDetailsQuery.isError) {
    return <FetchDataError error={probeDetailsQuery.error} />;
  }

  if (!stage || !processor) {
    return (
      <GenericErrors
        header='Invalid stage'
        errors='The stage does not exist'
      />
    );
  }

  return (
    <ProbeStage
      probe={probe}
      processor={processor}
      stage={stage}
      loaderContent={loaderContent}
      visibleColumns={widget.visible_columns}
      filter={(renderingStrategy?.filterDeserializer || castFilterConverter)(widget.filter)}
      anomalousOnly={widget.anomalous_only ?? false}
      spotlightMode={widget.spotlight_mode ?? false}
      showContextInfo={widget.show_context ?? false}
      dataSource={widget.data_source}
      timeSeriesDuration={widget.time_series_duration}
      aggregationType={widget.aggregation_type}
      timeSeriesAggregation={widget.aggregation_period}
      combineGraphs={widget.combine_graphs}
      valueColumnName={valueColumnName}
      compact
      noLastOptionsRestore
      stageLink={
        <Link
          reloadDocument
          to={{
            pathname: generateProbeURI({blueprintId, probeId: probe.id, stageName: stage.name}),
            search: getStageQueryParamsFromWidget({
              widget,
              stage,
              alwaysUseContext: renderingStrategy?.alwaysUseContext,
              filterDeserializer: renderingStrategy?.filterDeserializer
            })!,
          }}
        >
          {'View stage'}
        </Link>
      }
      activePage={1}
      pageSize={widget.max_items ?? DEFAULT_PAGE_SIZE}
      sorting={queryParamToSorting(widget.orderby)}
    />
  );
});

StageWidget.displayName = 'StageWidget';

export default StageWidget;
